import cn from 'classnames/bind';
import React, { FC, useEffect } from 'react';
import type { RouterLocation } from '@funtech-int/rival';
import { useAnalytics } from '@funtech-int/yango-shared';

import { RouterLink } from '@/shared/components/RouterLink/RouterLink';
import { Link } from '@/shared/components/Link/Link';
import { mainRoute } from '@/scenarios/home/routes';
import { getProfileSubText } from '@/scenarios/profiles/lib/getProfileSubText';
import { useI18n } from '@/scenarios/profiles/hooks/useI18n';
import { BrandAvatar } from '@/shared/components/BrandAvatar/BrandAvatar';
import { AvatarSize } from '@/shared/components/Avatar/Avatar';

import { createAnalyticsEvents } from '../../lib/createAnalyticsEvents';

import styles from './ProfileFamilyItem.module.css';

const cx = cn.bind(styles);

interface Props {
    text: string;
    url?: string;
    navigateTo?: RouterLocation<unknown, unknown>;
    avatarUrl?: string;
    isMasterProfile?: boolean;
    ageRestrictionGroup?: string;
    withAnimation?: boolean;
    avatarSize?: AvatarSize;
    isAdmin?: boolean;
    isAuthorized?: boolean;
    hasSubscription?: boolean;
    onAnimationPlay?: () => void;
    onClick?: () => void;
    viewVariant?: 'vertical' | 'horizontal';
    isProfilesPageAutoShowed?: boolean;
    isFamilyAccount?: boolean;
    isPreCreated?: boolean;
    id: number | null;
}

export const ProfileFamilyItem: FC<Props> = (props) => {
    const {
        text,
        url = '',
        navigateTo = mainRoute(),
        avatarUrl,
        isMasterProfile,
        ageRestrictionGroup,
        withAnimation,
        avatarSize,
        isAdmin,
        isAuthorized = true,
        hasSubscription,
        onAnimationPlay = () => undefined,
        onClick = () => undefined,
        viewVariant = 'vertical',
        isProfilesPageAutoShowed,
        isFamilyAccount,
        isPreCreated,
        id,
    } = props;
    const isHorizontalView = viewVariant === 'horizontal';

    const analytics = useAnalytics(createAnalyticsEvents);

    const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
        if (!url) {
            e.preventDefault();
        }

        analytics.profileSelected({
            autoShowed: Boolean(isProfilesPageAutoShowed),
            isPreGenerated: Boolean(isPreCreated),
            hasChildAnimation: Boolean(withAnimation),
            isFamilyAccount: Boolean(isFamilyAccount),
            isAuthorized: Boolean(isAuthorized),
            targetProfileId: id ? String(id) : '-',
            isTargetMaster: Boolean(isMasterProfile),
        });

        onClick();
    };

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (withAnimation) {
                onAnimationPlay();
            }
        }, 2000);

        return () => clearTimeout(timeoutId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const i18n = useI18n();

    const profileSubText = getProfileSubText({
        i18n,
        isAdmin,
        isAuthorized,
        isMasterProfile,
        ageRestrictionGroup,
        parentalControlEnabled: undefined,
    });

    const component = (
        <>
            <BrandAvatar
                avatarUrl={avatarUrl}
                avatarSize={avatarSize || 160}
                withSubProfileAnimation={withAnimation}
                className={cx('avatar')}
                hasSubscriptions={hasSubscription}
            />
            <div>
                <p className={cx('text')}>{text}</p>
                {profileSubText && <p className={cx('sub-text')}>{profileSubText}</p>}
            </div>
        </>
    );

    if (url) {
        return (
            <Link
                className={cx('root', {
                    'root-horizontal': isHorizontalView,
                })}
                href={url}
                onClick={handleClick}
            >
                {component}
            </Link>
        );
    }

    return (
        <RouterLink
            replace={!isMasterProfile}
            className={cx('root', {
                'root-with-animation': withAnimation,
                'root-horizontal': isHorizontalView,
            })}
            to={navigateTo}
            onClick={handleClick}
        >
            {component}
        </RouterLink>
    );
};
